import type { PeniellyItem, Proposal, QueravelArtifact } from "@rhema/client";
import { ACTION_TIMEOUT_MS } from "@rhema/client";
import {
  Button,
  cn,
  FileTextIcon,
  focusRing,
  LockIcon,
  PlayIcon,
  ProductMark,
  RichText,
  SendIcon,
} from "@rhema/ui";
import { type MouseEvent, type ReactNode, useCallback, useMemo } from "react";
import { useRichRender } from "../conversation/rich";
import { LinkForm } from "../links/LinkForm";
import { formatBytes, saveBlob } from "../media/prepare";
import type { LiveStep } from "./runner";
import type { QueravelSession } from "./useQueravel";

/**
 * Queravel's own app icon, the Signal Q on its forest-green tile. With a label it is named for
 * screen readers, and without one it is hidden from them.
 */
export function QueravelTile({ size = 30, label }: { size?: number; label?: string }) {
  return <ProductMark product="queravel" size={size} {...(label ? { label } : {})} />;
}

/** The plain account of where a question to Queravel goes. */
export const PRIVACY =
  "What you send to Queravel leaves Rhema's end-to-end encryption. Queravel may search the web to answer, but it does not keep what you send.";

/** The extra line on the check before a chat's messages go to Queravel. */
export const MAY_SEARCH = "Queravel may search the web while answering.";

export function PrivacyNotice({ className }: { className?: string }) {
  return (
    <p
      className={cn("m-0 flex items-start gap-1.5 text-[12.5px] leading-snug text-ink-secondary", className)}
    >
      <LockIcon size={13} strokeWidth={2.2} className="mt-px flex-none" />
      <span>{PRIVACY}</span>
    </p>
  );
}

export type Reference = { title: string; url?: string; domain?: string };

const web = (v: unknown): string | undefined =>
  typeof v === "string" && /^https?:\/\//i.test(v.trim()) ? v.trim() : undefined;

function domainOf(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, "") || undefined;
  } catch {
    return undefined;
  }
}

/**
 * References come in whatever shape Queravel sends. A web source arrives as
 * `{text, score, metadata: {filename, url, source_type}}`, with the page title as the filename.
 * Only a title and an http or https address are shown.
 */
export function referenceOf(value: unknown): Reference | null {
  if (typeof value === "string") return value.trim() ? { title: value.trim() } : null;
  if (!value || typeof value !== "object") return null;
  const r = value as Record<string, unknown>;
  const meta = r.metadata && typeof r.metadata === "object" ? (r.metadata as Record<string, unknown>) : {};
  const url = web(r.url) ?? web(meta.url);
  const domain = url ? domainOf(url) : undefined;
  const title = [r.title, meta.title, meta.filename, r.name, r.source, domain, url].find(
    (x): x is string => typeof x === "string" && !!x.trim(),
  );
  return title ? { title: title.trim(), ...(url ? { url } : {}), ...(domain ? { domain } : {}) } : null;
}

/** Each reference by its number in the answer, which starts at 1. */
function numbered(items: unknown[]): { n: number; ref: Reference }[] {
  return items.flatMap((item, i) => {
    const ref = referenceOf(item);
    return ref ? [{ n: i + 1, ref }] : [];
  });
}

/** The sources under an answer, numbered as the answer cites them. */
function Sources({ items, open }: { items: unknown[]; open: (href: string) => void }) {
  const refs = numbered(items);
  if (!refs.length) return null;
  return (
    <section aria-label="Sources" className="mt-0.5 flex flex-col gap-1">
      <h4 className="m-0 text-[12.5px] font-semibold text-ink-secondary">Sources</h4>
      <ol className="m-0 flex list-none flex-col gap-1 p-0 text-[13px]">
        {refs.map(({ n, ref }) => (
          <li key={n} className="flex min-w-0 items-baseline gap-2">
            <span className="w-4 flex-none text-right text-[12px] font-semibold text-queravel">{n}</span>
            <span className="min-w-0 flex-1 truncate">
              {ref.url ? (
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={(e) => {
                    e.preventDefault();
                    open(ref.url as string);
                  }}
                  className={cn("text-ink underline decoration-line-strong underline-offset-2", focusRing)}
                >
                  {ref.title}
                </a>
              ) : (
                <span className="text-ink-secondary">{ref.title}</span>
              )}
              {ref.domain && ref.domain !== ref.title && (
                <span className="ml-1.5 text-[12px] text-ink-meta">{ref.domain}</span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const CITE = /\[(\d{1,3}(?:\s*,\s*\d{1,3})*)\]/g;

/**
 * Turns the [1] and [1, 2] markers in an answer into small numbered links to its sources. A
 * marker for a source that has no web address stays as written.
 */
export function citations(
  refs: Map<number, Reference>,
  open: (href: string) => void,
): (text: string) => ReactNode {
  return (text) => {
    if (!text.includes("[")) return text;
    const out: ReactNode[] = [];
    let last = 0;
    for (const match of text.matchAll(CITE)) {
      const nums = (match[1] ?? "").split(",").map((x) => Number(x.trim()));
      if (!nums.every((n) => refs.get(n)?.url)) continue;
      const at = match.index ?? 0;
      // The word before the marker and the punctuation after it stay on one line with the number,
      // so a narrow screen never starts a line with a lone full stop.
      const before = text.slice(last, at);
      const tail = /(\S*)\s*$/.exec(before) ?? ["", ""];
      const word = tail[1] ?? "";
      const head = before.slice(0, before.length - tail[0].length);
      if (head) out.push(head);
      const after = /^[.,;:!?)]+/.exec(text.slice(at + match[0].length))?.[0] ?? "";
      const chips: ReactNode[] = [];
      for (const n of nums) {
        const ref = refs.get(n) as Reference;
        const url = ref.url as string;
        const click = (e: MouseEvent<HTMLAnchorElement>) => {
          e.preventDefault();
          e.stopPropagation();
          open(url);
        };
        chips.push(
          <a
            key={`${at}-${n}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            onClick={click}
            aria-label={`Source ${n}: ${ref.title}`}
            className={cn(
              "ml-0.5 inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-queravel-tint px-1 align-[2px] text-[11px] leading-none font-semibold text-queravel no-underline hover:bg-queravel hover:text-white",
              focusRing,
            )}
          >
            {n}
          </a>,
        );
      }
      out.push(
        <span key={`g-${at}`} className="whitespace-nowrap">
          {word}
          {chips}
          {after}
        </span>,
      );
      last = at + match[0].length + after.length;
    }
    if (!out.length) return text;
    if (last < text.length) out.push(text.slice(last));
    return out;
  };
}

const DONE: Record<string, [string, (n: number) => string]> = {
  web: ["Searched the web", (n) => `Searched the web ${n} times`],
  page: ["Read a page", (n) => `Read ${n} pages`],
  code: ["Ran code", (n) => `Ran code ${n} times`],
  map: ["Checked the map", (n) => `Checked the map ${n} times`],
  image: ["Made an image", (n) => `Made ${n} images`],
  other: ["Used a tool", (n) => `Used ${n} tools`],
};

function groupOf(tool: string): keyof typeof DONE {
  if (tool === "web_search") return "web";
  if (tool === "fetch_url") return "page";
  if (tool === "run_code") return "code";
  if (tool === "geocode" || tool === "route") return "map";
  if (tool === "generate_image") return "image";
  return "other";
}

/** What Queravel did for an answer, in one short line, such as "Searched the web · Read 2 pages". */
export function stepsSummary(tools: string[]): string {
  const counts = new Map<keyof typeof DONE, number>();
  for (const t of tools) {
    const g = groupOf(t);
    counts.set(g, (counts.get(g) ?? 0) + 1);
  }
  return [...counts]
    .map(([g, n]) => {
      const [one, many] = DONE[g] as [string, (n: number) => string];
      return n === 1 ? one : many(n);
    })
    .join(" · ");
}

/** The tool Queravel is using, shown where the answer will be until its words arrive. */
function StepNow({ step }: { step: LiveStep }) {
  return (
    <p role="status" className="m-0 flex min-w-0 items-center gap-2 text-[14.5px] text-queravel">
      <span aria-hidden="true" className="relative flex h-2 w-2 flex-none">
        <span className="absolute inset-0 animate-ping rounded-full bg-queravel-accent opacity-60" />
        <span className="relative h-2 w-2 rounded-full bg-queravel-accent" />
      </span>
      <span className="min-w-0 truncate">
        <span className="font-semibold">{step.label}</span>
        {step.query ? `: ${step.query}` : ""}
      </span>
    </p>
  );
}

/** Opens a file Queravel made, or saves it through the platform. */
function FileCards({ files, open }: { files: QueravelArtifact[]; open: (href: string) => void }) {
  const shown = files.filter((f): f is QueravelArtifact & { url: string } => Boolean(f.url));
  const save = useCallback(
    async (f: { name: string; url: string }) => {
      try {
        const res = await fetch(f.url);
        if (!res.ok) throw new Error(String(res.status));
        saveBlob(await res.blob(), f.name);
      } catch {
        // Some file hosts do not let the app read the file itself. The browser can still fetch it.
        open(f.url);
      }
    },
    [open],
  );
  if (!shown.length) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <ul aria-label="Files" className="m-0 flex list-none flex-col gap-2 p-0">
        {shown.map((f, i) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: files keep their order
            key={i}
            className="flex items-center gap-3 rounded-tile border border-line bg-surface px-3 py-2.5"
          >
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-field bg-queravel-tint text-queravel">
              <FileTextIcon size={18} />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-semibold">{f.name}</span>
              {f.size !== undefined && (
                <span className="text-[12.5px] text-ink-meta">{formatBytes(f.size)}</span>
              )}
            </span>
            <Button size="sm" variant="outline" aria-label={`Open ${f.name}`} onClick={() => open(f.url)}>
              Open
            </Button>
            <Button size="sm" variant="outline" aria-label={`Save ${f.name}`} onClick={() => void save(f)}>
              Save
            </Button>
          </li>
        ))}
      </ul>
      <p className="m-0 text-[12px] text-ink-meta">
        {shown.length === 1 ? "The link to this file expires" : "Links to these files expire"} about an hour
        after Queravel made {shown.length === 1 ? "it" : "them"}. Save {shown.length === 1 ? "it" : "them"} to
        keep a copy.
      </p>
    </div>
  );
}

/** One answer from Queravel, with its mark beside it. */
export function QueravelAnswer({
  text,
  references = [],
  writing = false,
  step = null,
  tools = [],
  artifacts = [],
  children,
}: {
  text: string;
  references?: unknown[];
  writing?: boolean;
  /** The tool Queravel is using right now, while it writes. */
  step?: LiveStep | null;
  /** The tools Queravel has finished using for this answer. */
  tools?: string[];
  artifacts?: QueravelArtifact[];
  children?: ReactNode;
}) {
  // Answers are written in a different Markdown from Rhema's, so they show as written, with web
  // and email addresses made into links that open in the browser.
  const render = useRichRender();
  const { openExternal } = render;
  const signature = JSON.stringify(references);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the signature stands for the references
  const renderText = useMemo(() => {
    const refs = new Map(numbered(references).map(({ n, ref }) => [n, ref]));
    return [...refs.values()].some((r) => r.url) ? citations(refs, openExternal) : undefined;
  }, [signature, openExternal]);
  const summary = stepsSummary(tools);
  return (
    <div className="flex items-start gap-2.5">
      <QueravelTile size={28} label="Queravel" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        {summary && <p className="m-0 text-[12.5px] text-ink-meta">{summary}</p>}
        {text ? (
          <div className="m-0 text-[15px] leading-normal whitespace-pre-wrap">
            <RichText body={text} {...render} {...(renderText ? { renderText } : {})} />
          </div>
        ) : writing && step ? (
          <StepNow step={step} />
        ) : writing ? (
          <p className="m-0 text-[15px] text-ink-meta">Queravel is writing</p>
        ) : null}
        <Sources items={references} open={openExternal} />
        <FileCards files={artifacts} open={openExternal} />
        {children}
      </div>
    </div>
  );
}

function Thumbs({ photos }: { photos: PeniellyItem[] }) {
  if (!photos.length) return null;
  return (
    <ul aria-label="Photos" className="m-0 flex list-none flex-wrap gap-1.5 p-0">
      {photos.map((p) => (
        <li key={p.id} className="relative h-16 w-[84px] overflow-hidden rounded-[10px] bg-surface-header">
          {p.thumb_url ? (
            <img
              src={p.thumb_url}
              alt={p.place ? `Photo from Penielly, ${p.place}` : "Photo from Penielly"}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-xs text-ink-meta">
              {p.kind === "video" ? "Video" : "Photo"}
            </span>
          )}
          {p.kind === "video" && (
            <PlayIcon size={14} className="absolute right-1.5 bottom-1.5 text-white" aria-hidden="true" />
          )}
          <span className="absolute bottom-1 left-1">
            <ProductMark product="penielly" size={16} />
          </span>
        </li>
      ))}
    </ul>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="w-[60px] flex-none text-ink-meta">{label}</span>
      <span className="min-w-0 flex-1 font-semibold break-words">{children}</span>
    </div>
  );
}

const MINUTES = Math.round(ACTION_TIMEOUT_MS / 60_000);

function outcome(p: Proposal): string | null {
  const timedOut = p.state === "timeout" || p.error === "timed out";
  if (p.state === "running") return p.name === "malakh.draft" ? "Saving the draft" : "Sending";
  if (timedOut) return `Nothing was done. The offer ended after ${MINUTES} minutes.`;
  if (p.state === "rejected") return "Cancelled. Nothing was sent.";
  if (p.state === "failed") return `This did not work: ${p.error ?? "something went wrong"}.`;
  if (p.state === "done") {
    if (p.name === "rhema.send") return `Sent to ${p.chosen?.label ?? "the chat"}.`;
    if (p.name === "malakh.draft") return "Draft saved in Malakh. It has not been sent.";
  }
  return null;
}

/** A write Queravel proposed. Nothing happens until the person presses the button. */
function WriteCard({
  proposal: p,
  session,
}: {
  proposal: Extract<Proposal, { write: true }>;
  session: Pick<QueravelSession, "confirm" | "reject" | "choose">;
}) {
  const pending = p.state === "pending";
  const done = outcome(p);
  const send = p.name === "rhema.send";
  return (
    <article aria-label={p.label} className="overflow-hidden rounded-tile border border-line bg-surface">
      <div className="flex items-center gap-2 border-b border-line px-3.5 py-2.5">
        {send ? (
          <SendIcon size={17} strokeWidth={2.2} className="flex-none text-blue" />
        ) : (
          <ProductMark product="malak" size={22} />
        )}
        <span className="flex-1 text-[13.5px] font-bold">{p.label}</span>
      </div>
      <div className="flex flex-col gap-1.5 px-3.5 py-3">
        {send ? (
          <>
            {p.chosen ? (
              <Row label="To">
                {p.chosen.label}
                {p.chosen.detail && (
                  <span className="ml-1.5 font-normal text-ink-secondary">{p.chosen.detail}</span>
                )}
              </Row>
            ) : null}
            {pending && p.recipients.length > 1 && (
              <fieldset className="m-0 mt-1 flex flex-col gap-1 border-0 p-0">
                <legend className="mb-1 p-0 text-sm text-ink-secondary">
                  More than one chat matches "{p.args.to}". Choose one.
                </legend>
                {p.recipients.map((r) => (
                  <label
                    key={r.chatId}
                    className="flex cursor-pointer items-center gap-2 rounded-field px-2 py-1.5 text-sm hover:bg-linen"
                  >
                    <input
                      type="radio"
                      name={`to-${p.id}`}
                      checked={p.chosen?.chatId === r.chatId}
                      onChange={() => session.choose(p.id, r.chatId)}
                    />
                    <span className="font-semibold">{r.label}</span>
                    {r.detail && <span className="text-ink-secondary">{r.detail}</span>}
                  </label>
                ))}
              </fieldset>
            )}
            {p.args.text && (
              <p className="m-0 mt-1 font-serif text-base leading-[1.45] whitespace-pre-wrap">
                {p.args.text}
              </p>
            )}
          </>
        ) : (
          <>
            <Row label="To">{p.args.to.join(", ")}</Row>
            {p.args.subject && <Row label="Subject">{p.args.subject}</Row>}
            {p.args.body && (
              <p className="m-0 mt-1 font-serif text-base leading-[1.45] whitespace-pre-wrap">
                {p.args.body}
              </p>
            )}
          </>
        )}
        <Thumbs photos={p.photos} />
      </div>
      {pending ? (
        <div className="flex flex-col gap-2 px-3.5 pb-3.5">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => session.reject(p.id)}>
              Cancel
            </Button>
            <Button
              size="sm"
              className="flex-[2]"
              disabled={send && !p.chosen}
              onClick={() => session.confirm(p.id)}
            >
              {send ? "Send" : "Save draft"}
            </Button>
          </div>
          <p className="m-0 text-xs text-ink-meta">
            {send
              ? `Nothing is sent until you press Send. This offer ends after ${MINUTES} minutes.`
              : `The draft is only saved, never sent. This offer ends after ${MINUTES} minutes.`}
          </p>
        </div>
      ) : (
        done && (
          <p role="status" className="m-0 px-3.5 pb-3 text-[13px] text-ink-secondary">
            {done}
          </p>
        )
      )}
    </article>
  );
}

/** The actions Queravel proposed in this conversation. Reads show as one quiet line. */
export function Proposals({
  proposals,
  session,
}: {
  proposals: Proposal[];
  session: Pick<QueravelSession, "confirm" | "reject" | "choose">;
}) {
  if (!proposals.length) return null;
  return (
    <div className="flex flex-col gap-2">
      {proposals.map((p) =>
        p.write ? (
          <WriteCard key={p.id} proposal={p} session={session} />
        ) : (
          <p key={p.id} className="m-0 text-[12.5px] text-ink-meta">
            {p.name === "penielly.search"
              ? p.state === "failed"
                ? "Queravel tried to search your photos, but the search did not work."
                : "Queravel searched your photos"
              : "Queravel asked for something Rhema cannot do here."}
          </p>
        ),
      )}
    </div>
  );
}

/** Errors, the allowance message and the link form, under the answer they belong to. */
export function SessionProblem({ session }: { session: QueravelSession }) {
  if (session.status === "quota") {
    return (
      <p role="alert" className="m-0 rounded-field bg-gold-tint px-3.5 py-2.5 text-sm text-gold-ink">
        {session.error}
      </p>
    );
  }
  if (session.status === "not_linked") {
    return (
      <div className="rounded-tile border border-line bg-surface px-4 py-3.5">
        <LinkForm product="queravel" onDone={session.retry} />
      </div>
    );
  }
  if (session.status === "error") {
    return (
      <div
        role="alert"
        className="flex flex-wrap items-center gap-2 rounded-field bg-scarlet-tint px-3.5 py-2.5"
      >
        <span className="min-w-0 flex-1 text-sm text-scarlet">{session.error}</span>
        <Button size="sm" variant="outline" onClick={session.retry}>
          Try again
        </Button>
      </div>
    );
  }
  return null;
}
